import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { ReactComponent as WaChannelSVG } from '@common-assets/waChannel.svg';
import { ReactComponent as WaGroupSVG } from '@common-assets/waGroup.svg';
import { ReactComponent as WaPersonSVG } from '@common-assets/waPerson.svg';
import PersonAvatar from '@common-components/tasks/person-avatar/person-avatar';
import { calculatedWaContactName } from '@common-reducers/WhatsAppSelectors';
import { getWaContactBySerializedId } from '@common-reducers/wa-contact-entity-lookup';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { CommonRootState } from '@common/types/common-root-state-type';
import { profilePicExpiredEmitter } from '@extension/context/content/hooks/profilePicExpiredEmitter';
import { profilePicFetchRegistry } from '@extension/context/content/hooks/profilePicFetchRegistry';
import { createProfilePicBatcher } from '@extension/context/content/hooks/profilePicFetchBatcher';
import { resolveAvatarPicture, shouldExpireCachedPicture } from './contact-avatar-picture';
import { clearExpiredProfilePicThunk, fetchAndCacheProfilePicUrlsThunk } from '@extension/context/content/thunks/WhatsappThunks';
import { Box, SxProps, Theme, Tooltip } from '@mui/material';
import { CSSProperties, memo, useCallback, useEffect, useState } from 'react';

export interface ContactAvatarProps {
    contactId: string;
    size?: number;
    textSize?: number;
    sx?: SxProps<Theme>,
    /** A picture that OVERRIDES the contact cache. */
    profilePicURL?: string;
    /**
     * A picture to use only when the contact cache has none — e.g. the URL a
     * scheduled message stored for its recipient when it was composed, which is
     * a hint, not the truth: WhatsApp picture URLs expire. Unlike
     * `profilePicURL`, a fresh cached picture wins over it, and if it fails to
     * load the avatar simply stops using it (and may fetch a fresh one) instead
     * of treating the CACHED picture as expired.
     */
    fallbackPicURL?: string;
    dontFetchProfilePicFromDB?: boolean;
    avatarStyle?: CSSProperties;
    forceRefresh?: boolean;
}

// One request per burst of avatars, not one per avatar. Keyed by dispatch: the
// extension content script runs two stores side by side, and an id must be
// fetched and cached through the store that asked for it.
const profilePicBatcher = createProfilePicBatcher<(action: any) => any>({
    onFailure: ids => ids.forEach(id => profilePicFetchRegistry.remove(id)),
});

const ContactAvatar = memo(({
    contactId,
    size = 55,
    textSize = 16,
    sx,
    profilePicURL,
    fallbackPicURL,
    avatarStyle,
    dontFetchProfilePicFromDB = false,
}: ContactAvatarProps) => {
    const dispatch = useAppDispatch();
    const [failedUrl, setFailedUrl] = useState<string>();

    const contactSelector = useCallback((state: CommonRootState) => {
        const entities = state.DBWaContactsCacheReducer?.entities || {};
        const contact = getWaContactBySerializedId(entities, contactId);
        return {
            name: contact ? calculatedWaContactName(contact) : undefined,
            profilePic: contact?.imgUrl,
            btProfilePic: state.CalendarReducer?.contactPicMap?.[contactId],
            profilePicLastUpdate: contact?.profilePicLastUpdate,
            isGroup: chatKindOf(contactId) === 'group',
            isChannel: chatKindOf(contactId) === 'channel',
        };
    }, [contactId]);

    const contactData = useAppSelector(contactSelector);
    // The fallback is used only while nothing better exists, and never again
    // once it has failed to load — see contact-avatar-picture.ts.
    const picture = resolveAvatarPicture({
        profilePicURL,
        cachedPic: contactData.profilePic,
        mappedPic: contactData.btProfilePic,
        fallbackPicURL,
        failedUrl,
    });
    const finalProfilePic = picture.src;
    const hasError = picture.failed;
    const expireOnError = shouldExpireCachedPicture(picture);

    // Self-fetch the profile pic via waGetProfilePicUrlTool (works in both the
    // extension and the website) when we don't already have one. This makes the
    // avatar self-sufficient everywhere — not just inside the contact list,
    // where useWaContactsLazyLoad batch-fetches. Callers that already batch
    // (the virtualized list rows) opt out via dontFetchProfilePicFromDB.
    // Fetch de-dup/TTL is shared with the hook through profilePicFetchRegistry.
    useEffect(() => {
        if (dontFetchProfilePicFromDB) return;
        if (!contactId) return;
        if (finalProfilePic) return;
        // Channels (@newsletter) never have profile pics — don't waste a fetch.
        if (contactData.isChannel) return;
        if (profilePicFetchRegistry.has(contactId)) return;
        if (profilePicFetchRegistry.isFresh(contactData.profilePicLastUpdate)) return;

        profilePicFetchRegistry.add(contactId);
        profilePicBatcher.request(dispatch, ids => dispatch(fetchAndCacheProfilePicUrlsThunk(ids)).unwrap(), contactId);
    }, [dontFetchProfilePicFromDB, contactId, finalProfilePic, contactData.isChannel, contactData.profilePicLastUpdate, dispatch]);

    const handleImageError = useCallback(() => {
        setFailedUrl(finalProfilePic);
        // A stale FALLBACK says nothing about the cache: the cache had no picture
        // (that is why the fallback was showing), so there is nothing to clear
        // and no server write to make. Dropping the fallback is enough — with
        // no picture left, the self-fetch above asks for a fresh one.
        if (!expireOnError) return;
        if (contactId) {
            dispatch(clearExpiredProfilePicThunk(contactId));
            // Allow exactly one re-fetch of the now-cleared URL — both for our
            // own self-fetch effect and for any mounted lazy-load hook.
            profilePicFetchRegistry.allowRetry(contactId);
            profilePicExpiredEmitter.markExpired(contactId);
        }
    }, [contactId, dispatch, finalProfilePic, expireOnError]);

    return (
        <Tooltip title={contactData.name}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', ...sx }}>
                {finalProfilePic && !hasError ? (
                    <PersonAvatar
                        textSize={textSize}
                        size={size}
                        profileImg={finalProfilePic}
                        bgcolor='var(--color-surface-muted)'
                        avatarStyle={avatarStyle}
                        onImageError={handleImageError}
                    />
                ) : contactData.isGroup ? (
                    // viewBox is asserted explicitly because SVGO strips it from
                    // sources that declare both width and height — which leaves
                    // the SVG in raw user units and clips it to the top-left
                    // corner. All three default-avatar SVGs author-side are 212×212.
                    <WaGroupSVG height={size} width={size} viewBox="0 0 212 212" />
                ) : contactData.isChannel ? (
                    <WaChannelSVG height={size} width={size} viewBox="0 0 212 212" />
                ) : (
                    <WaPersonSVG height={size} width={size} viewBox="0 0 212 212" />
                )}
            </Box>
        </Tooltip>
    );
});

ContactAvatar.displayName = 'ContactAvatar';
export default ContactAvatar;
